import { MenuItem, Popover } from '@material-ui/core';
import { createKeyPath } from 'CMFW/context/helper/keyContext';
import KeyContext from 'CMFW/context/KeyContext';
import ModuleContext from 'CMFW/context/ModuleContext';
import { THE_COLOR_OF_CHANGE } from 'components/common/styles';
import React, { useContext, useRef, useState } from 'react';
import styled from 'styled-components';

type IRefLevelScale = {
  label: string;
  levels: number[];
  colors: Record<number, string>;
};

// Reference-level scale per dialect; add an entry here to enable the select for a new language.
const REF_LEVEL_SCALES: Record<number, IRefLevelScale> = {
  10: {
    label: 'HSK',
    levels: [1, 2, 3, 4, 5, 6, 7, 8, 9],
    // 1-6 sampled from the HSK Standard Course (BLCU) book covers; 7-9 have no per-level book color
    colors: {
      1: '#f8b018',
      2: '#0890a0',
      3: '#e86800',
      4: '#b80810',
      5: '#183870',
      6: '#582850',
      7: '#78909c',
      8: '#546e7a',
      9: '#37474f',
    },
  },
};

// Whether a dialect carries item metadata (transcription + ref level). Used as
// visibility fallback where the backend flags are not available (new panels).
export const hasRefLevelScale = (dialectId: number): boolean => Boolean(REF_LEVEL_SCALES[dialectId]);

const DEFAULT_SCALE: IRefLevelScale = {
  label: 'level',
  levels: [1, 2, 3, 4, 5, 6, 7, 8, 9],
  colors: {},
};

const FALLBACK_COLOR = '#9e9e9e';

const darken = (hex: string, factor = 0.72) =>
  '#' +
  [1, 3, 5]
    .map((i) =>
      Math.round(parseInt(hex.slice(i, i + 2), 16) * factor)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('');

type IChipProps = {
  dialectId: number;
  value: number; // 0 = no level
  onSelect: (level: number) => void;
  changed?: boolean;
  className?: string;
};

/**
 * Presentational ref-level chip + popover selector (the house HSK selector),
 * decoupled from the CMFW module framework so plain-React modules (Concept
 * Tree ficha) share the exact voc/panels visual and behavior.
 */
export const RefLevelChip: React.FC<IChipProps> = ({ dialectId, value, onSelect, changed = false, className }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const chipRef = useRef<HTMLDivElement>(null);

  const scale = REF_LEVEL_SCALES[dialectId] ?? DEFAULT_SCALE;
  const levelColor = (level: number) => scale.colors[level] ?? FALLBACK_COLOR;

  const handleClick = () => {
    if (chipRef.current) {
      setAnchorEl(chipRef.current);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (level: number) => {
    onSelect(level);
    setAnchorEl(null);
  };

  return (
    <Wrapper className={className} $changed={changed}>
      <Chip ref={chipRef} $color={value > 0 ? levelColor(value) : null} onClick={handleClick} title={scale.label}>
        {value > 0 ? value : '–'}
      </Chip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItemStyled selected={value === 0} onClick={() => handleSelect(0)}>
          <Swatch $color={null}>–</Swatch>
          <Use>no level</Use>
        </MenuItemStyled>
        {scale.levels.map((level) => (
          <MenuItemStyled key={level} selected={level === value} onClick={() => handleSelect(level)}>
            <Swatch $color={levelColor(level)}>{level}</Swatch>
            <Use>{scale.label}</Use>
          </MenuItemStyled>
        ))}
      </Popover>
    </Wrapper>
  );
};

type IProps = {
  dialectId: number;
  className?: string;
};

const VocRefLevelSelect: React.FC<IProps> = ({ dialectId, className }) => {
  const parentKey = useContext(KeyContext);
  const refLevelKey = createKeyPath(parentKey, 'refLevel');

  const module = useContext(ModuleContext);
  const currentLevel = Number(module.getItemUpdatedValue(refLevelKey) ?? 0);
  const originalLevel = Number(module.getItemValue(refLevelKey) ?? 0);

  return (
    <RefLevelChip
      className={className}
      dialectId={dialectId}
      value={currentLevel}
      changed={currentLevel !== originalLevel}
      onSelect={(level) => module.updateItemValue.bind(module)(refLevelKey, level)}
    />
  );
};

const Wrapper = styled.div<{ $changed: boolean }>`
  flex-shrink: 0;
  align-self: stretch;
  display: flex;
  padding: 3px;
  margin: -3px 0;
  border-radius: 6px;
  background-color: ${(props) => (props.$changed ? THE_COLOR_OF_CHANGE : 'transparent')};
`;

const levelBox = `
  width: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
  box-sizing: border-box;
  user-select: none;
`;

const Chip = styled.div<{ $color: string | null }>`
  ${levelBox}
  cursor: pointer;
  background-color: ${(props) => (props.$color ? `${props.$color}66` : 'transparent')};
  color: ${(props) => (props.$color ? darken(props.$color) : 'rgba(0, 0, 0, 0.4)')};
  border: ${(props) => (props.$color ? 'none' : '1px solid rgba(0, 0, 0, 0.23)')};

  &:hover {
    background-color: ${(props) => (props.$color ? `${props.$color}8c` : 'transparent')};
    border-color: rgba(0, 0, 0, 0.87);
  }
`;

const Swatch = styled.div<{ $color: string | null }>`
  ${levelBox}
  height: 28px;
  margin-right: 8px;
  background-color: ${(props) => (props.$color ? `${props.$color}66` : 'transparent')};
  color: ${(props) => (props.$color ? darken(props.$color) : 'rgba(0, 0, 0, 0.4)')};
  border: ${(props) => (props.$color ? 'none' : '1px solid rgba(0, 0, 0, 0.23)')};
`;

const MenuItemStyled = styled(MenuItem)`
  && {
    font-size: 1rem;
    padding: 6px 12px;
    min-height: 32px;
  }
`;

const Use = styled.span`
  color: #666;
`;

export default VocRefLevelSelect;
